import { apiOrigin } from "./basket-validate";

// `GET /widget/school/:id/checkout-config`, fetched once for the page and
// shared: a pass or product overlay reads it, then the basket checkout it
// opens reads the same answer instead of asking again. A failed fetch isn't
// kept (the next caller asks again), and an answer is kept for a few
// minutes, so a page left open picks up a school's changed settings.

const KEEP_MS = 5 * 60 * 1000;
const DEFAULT_API_URL = "https://api.baluu.io";

type Entry = { at: number; answer: Promise<Record<string, unknown>> };
const cache = new Map<string, Entry>();

/** The school's checkout settings; rejects when they can't be had. */
export function fetchSchoolCheckoutConfig<T = Record<string, unknown>>(businessId: string, apiUrl?: string): Promise<T> {
  const base = apiOrigin(apiUrl || DEFAULT_API_URL);
  const key = `${base}|${businessId}`;
  const now = Date.now();
  const held = cache.get(key);
  if (held && now - held.at < KEEP_MS) return held.answer as Promise<T>;
  const answer = fetch(`${base}/widget/school/${encodeURIComponent(businessId)}/checkout-config`).then(async (r) => {
    if (!r.ok) throw new Error(`checkout-config ${r.status}`);
    const data = await r.json();
    if (!data || typeof data !== "object") throw new Error("checkout-config: no settings");
    return data as Record<string, unknown>;
  });
  cache.set(key, { at: now, answer });
  answer.catch(() => {
    if (cache.get(key)?.answer === answer) cache.delete(key);
  });
  return answer as Promise<T>;
}

/** For the tests. */
export function clearSchoolCheckoutConfigCache(): void {
  cache.clear();
}
